import { Editor } from "@monaco-editor/react";
import { Cancel, Edit, Save } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import { useQAManagerFile, useQAManagerUpdateFile } from "../../hooks/use-qa-manager";

interface FileViewerProps {
  sessionId: string;
  fileId: string;
}

export const FileViewer: React.FC<FileViewerProps> = ({ sessionId, fileId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");

  const { data: file, isLoading } = useQAManagerFile(sessionId, fileId);
  const updateMutation = useQAManagerUpdateFile();

  const handleEdit = () => {
    if (file) {
      setEditedContent(file.contents);
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        sessionId,
        fileId,
        contents: editedContent,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update file:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent("");
  };

  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        <CircularProgress size={12} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Loading file...
        </Typography>
      </Stack>
    );
  }

  if (!file) {
    return <Alert severity="error">File not found</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          {file.name}
        </Typography>
        {!isEditing ? (
          <IconButton size="small" onClick={handleEdit}>
            <Edit fontSize="small" />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to update file
        </Alert>
      )}

      {isEditing ? (
        <TextField
          fullWidth
          multiline
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          minRows={10}
          maxRows={30}
          sx={{
            fontFamily: "monospace",
            fontSize: "0.875rem",
          }}
        />
      ) : (
        <Box
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 1,
            border: 1,
            borderColor: "grey.300",
            maxHeight: "400px",
            overflow: "auto",
          }}
        >
          {file.name.endsWith("json") ? (
            <Editor
              height="400px"
              language="json"
              value={JSON.stringify(JSON.parse(file.contents), null, 2)}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                lineNumbers: "off",
                wordWrap: "on",
                wrappingIndent: "same",
              }}
              theme="vs-light"
            />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {file.contents}
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Created: {new Date(file.created_at).toLocaleString()}
        {file.updated_at && ` | Updated: ${new Date(file.updated_at).toLocaleString()}`}
      </Typography>
    </Box>
  );
};
